import { Link, useParams, useSearchParams } from "@/lib/nav";
import { useState } from "react";
import { BoardingPass } from "@/components/BoardingPass";
import { Chat } from "@/components/Chat";
import { BoardHeader, BoardShell, Loading, NotFoundBoard } from "@/components/Chrome";
import { StepBar } from "@/components/StepBar";
import { googleCalendarUrl, mapsUrl } from "@/lib/links";
import { usePassView } from "@/lib/store";

/** 個人登機證頁 —— 報名完成的最終畫面，也是之後回訪、入場出示的頁面 */
export default function PassPage() {
  const { token } = useParams<{ token: string }>();
  const isNew = useSearchParams().has("new");
  const view = usePassView(token);

  const [copied, setCopied] = useState(false);

  if (view === undefined) {
    return (
      <BoardShell>
        <BoardHeader />
        <Loading />
      </BoardShell>
    );
  }

  if (view === null) {
    return (
      <BoardShell>
        <BoardHeader />
        <NotFoundBoard message="找不到這張登機證，請確認代碼是否正確（或已被取消）。" />
      </BoardShell>
    );
  }

  const { attendee, flight, companions, tableMates } = view;
  const maps = mapsUrl(flight);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + `/pass/${attendee.passToken}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <BoardShell>
      <div className="no-print">
        <BoardHeader sub={`${flight.code} ${flight.title}`} />
        {isNew && <StepBar current={3} standby={attendee.status === "standby"} />}

        {attendee.status === "standby" && (
          <p className="tint tint-warn mb-4">
            你目前是候補——有人取消時主辦人會依序遞補，遞補後就能選位。請保留這張登機證。
          </p>
        )}
        {attendee.status === "confirmed" && !attendee.seat && (
          <div className="tint tint-accent mb-4 flex flex-wrap items-center justify-between gap-3">
            <span>已為你保留名額，還差一步——選個好位子！</span>
            <Link
              href={`/flight/${flight.code}/seat?pass=${attendee.passToken}`}
              className="btn btn-primary px-4 py-2 text-[13px]"
            >
              去選位
            </Link>
          </div>
        )}
      </div>

      <BoardingPass flight={flight} attendee={attendee} animate={isNew} />

      {companions.length > 0 && (
        <div className="no-print card mx-auto mt-4 w-full max-w-[420px] px-5 py-4">
          <div className="text-sub text-[12px] font-medium">同行旅客 · 把各自的登機證傳給他們</div>
          <div className="mt-2 flex flex-col gap-1.5">
            {companions.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3">
                <span className="text-[14px] font-semibold">
                  {c.name}
                  <span className="text-accent ml-2 font-bold">{c.seat ?? "候補"}</span>
                </span>
                <Link href={`/pass/${c.passToken}`} className="text-accent text-[13px] font-medium">
                  查看登機證 →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/*
        同桌的人：聚餐的重點是認識隔壁那位，而現場最尷尬的就是不知道他是誰、做什麼的。
        產業是報名時的必填欄位，就是為了這一段。
        只顯示公開欄位 —— 忌口與備註是給主辦人和餐廳看的。
      */}
      {tableMates.length > 0 && (
        <div className="no-print card mx-auto mt-4 w-full max-w-[420px] px-5 py-4">
          <div className="text-sub text-[12px] font-medium">
            同桌的人 · 第 {tableMates[0].seat?.slice(0, -1)} 桌
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            {tableMates.map((p) => (
              <div key={p.id} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 text-[14px]">
                  <span className="font-semibold">{p.name}</span>
                  {p.industry && <span className="text-sub ml-2 text-[13px]">{p.industry}</span>}
                </span>
                <span className="text-accent shrink-0 text-[13px] font-bold tabular-nums">
                  {p.seat}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="no-print mx-auto mt-4 w-full max-w-[420px]">
        <Chat token={attendee.passToken} />
      </div>

      <div className="no-print mx-auto mt-5 flex w-full max-w-[420px] flex-col gap-2.5">
        <button onClick={() => window.print()} className="btn btn-primary w-full">
          列印 / 存成 PDF
        </button>

        {/* 行事曆與導航：活動當天最常用的兩個動作 */}
        <div className="flex gap-2.5">
          <a
            href={googleCalendarUrl(flight, attendee)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary flex-1 text-[14px]"
          >
            加入日曆
          </a>
          {maps && (
            <a
              href={maps}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary flex-1 text-[14px]"
            >
              開啟地圖
            </a>
          )}
        </div>

        <div className="flex gap-2.5">
          <button onClick={copyLink} className="btn btn-secondary flex-1 text-[14px]">
            {copied ? "✓ 已複製" : "複製連結"}
          </button>
          <Link href={`/flight/${flight.code}`} className="btn btn-secondary flex-1 text-[14px]">
            活動資訊
          </Link>
        </div>
        <p className="text-sub mt-1 text-center text-[12px] leading-relaxed">
          聚餐當天請出示 QR CODE 完成報到，建議加入書籤或截圖保存。
        </p>
      </div>
    </BoardShell>
  );
}
